/**
 * TierUpgradeDowngradePage — `/franchise/tiers/:franchiseId/upgrade`.
 *
 * Backed by the dedicated `/v1/franchise/tier-changes` surface — request /
 * approve / apply lifecycle. Approving + applying mutates franchise.tierCode.
 */
import { type JSX, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  applyTierChange,
  approveTierChange,
  getFranchise,
  listTierChanges,
  listTiers,
  rejectTierChange,
  requestTierChange,
  type TierCode,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const TIER_RANK: Record<TierCode, number> = { TIER_3: 3, TIER_2: 2, TIER_1: 1 };

export function TierUpgradeDowngradePage(): JSX.Element {
  const qc = useQueryClient();
  const { franchiseId = '' } = useParams<{ franchiseId: string }>();
  const [targetTier, setTargetTier] = useState<TierCode | ''>('');
  const [reason, setReason] = useState('');
  const [actionErr, setActionErr] = useState<string | null>(null);

  const franchise = useQuery({
    queryKey: ['franchise', 'tier-change', franchiseId],
    queryFn: () => getFranchise(franchiseId),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const tiers = useQuery({
    queryKey: ['franchise', 'tiers'],
    queryFn: () => listTiers({ active: true }),
    retry: false,
  });

  const history = useQuery({
    queryKey: ['franchise', 'tier-change', 'history', franchiseId],
    queryFn: () => listTierChanges({ franchiseId }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const submit = useMutation({
    mutationFn: () =>
      requestTierChange(franchiseId, {
        toTier: targetTier as TierCode,
        reason,
      }),
    onSuccess: () => {
      setReason('');
      setTargetTier('');
      void qc.invalidateQueries({ queryKey: ['franchise', 'tier-change'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const approve = useMutation({
    mutationFn: (id: string) => approveTierChange(id, 'Approved via operator console.'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['franchise', 'tier-change'] }),
    onError: (e: Error) => setActionErr(e.message),
  });

  const reject = useMutation({
    mutationFn: (id: string) => rejectTierChange(id, 'Rejected via operator console.'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['franchise', 'tier-change'] }),
    onError: (e: Error) => setActionErr(e.message),
  });

  const apply = useMutation({
    mutationFn: (id: string) => applyTierChange(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['franchise', 'tier-change'] }),
    onError: (e: Error) => setActionErr(e.message),
  });

  if (franchiseId.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Tier change" />
        <p className="text-sm text-muted">Missing franchise id.</p>
      </div>
    );
  }

  if (franchise.isPending || tiers.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Tier change" />
        <p className="text-sm text-muted">Loading…</p>
      </div>
    );
  }

  if (franchise.isError) {
    const denied = franchise.error instanceof ApiError &&
      (franchise.error.status === 401 || franchise.error.status === 403);
    return (
      <div className="space-y-6">
        <PageHeader title="Tier change" />
        <DeferredNotice title={denied ? 'Permission required' : 'Service unreachable'}>
          {(franchise.error as Error).message}
        </DeferredNotice>
      </div>
    );
  }

  const f = franchise.data;
  const currentRank = TIER_RANK[f.tierCode];
  const targetRank = targetTier === '' ? null : TIER_RANK[targetTier];
  const direction =
    targetRank === null
      ? null
      : targetRank < currentRank
        ? 'upgrade'
        : targetRank > currentRank
          ? 'downgrade'
          : 'noop';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tier change request"
        description={`${f.legalName} · ${f.franchiseNumber}`}
        actions={<StatusBadge status={f.status} />}
      />

      <section className="bg-white border border-border rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted">Current tier</p>
            <p className="text-lg font-semibold text-gray-900" data-testid="current-tier">{f.tierCode}</p>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
              Target tier
            </label>
            <select
              value={targetTier}
              onChange={(e) => setTargetTier(e.target.value as TierCode | '')}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              data-testid="target-tier-select"
            >
              <option value="">Select…</option>
              {tiers.data?.items
                .filter((t) => t.code !== f.tierCode)
                .map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.code} — {t.displayName}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-muted mb-1">
            Justification
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            data-testid="tier-reason-textarea"
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              setActionErr(null);
              submit.mutate();
            }}
            disabled={
              targetTier === '' ||
              reason.trim().length === 0 ||
              direction === 'noop' ||
              submit.isPending
            }
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="tier-submit-btn"
          >
            {direction === 'upgrade' ? (
              <ArrowUpRight className="w-4 h-4" />
            ) : direction === 'downgrade' ? (
              <ArrowDownRight className="w-4 h-4" />
            ) : null}
            {submit.isPending ? 'Submitting…' : 'Submit change request'}
          </button>
          {direction === 'noop' ? (
            <p className="text-xs text-muted">Target equals current tier.</p>
          ) : null}
        </div>
      </section>

      <section className="bg-white border border-border rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Change history</h2>
        {history.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : history.isError ? (
          <DeferredNotice title="Service unreachable">{(history.error as Error).message}</DeferredNotice>
        ) : (history.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted">No change requests yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-3 py-2">From → To</th>
                <th className="text-left px-3 py-2">Direction</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">Requested</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {history.data?.map((c) => (
                <tr key={c.id} data-testid="tier-change-row" className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    {c.fromTier} → {c.toTier}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700">{c.direction}</td>
                  <td className="px-3 py-2"><StatusBadge status={c.status} /></td>
                  <td className="px-3 py-2 text-xs text-muted">{c.reason}</td>
                  <td className="px-3 py-2 text-xs text-muted">
                    {new Date(c.requestedAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    {c.status === 'REQUESTED' ? (
                      <>
                        <button
                          type="button"
                          onClick={() => approve.mutate(c.id)}
                          className="text-xs font-semibold text-nipost-green hover:underline"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => reject.mutate(c.id)}
                          className="text-xs font-semibold text-danger hover:underline"
                        >
                          Reject
                        </button>
                      </>
                    ) : c.status === 'APPROVED' ? (
                      <button
                        type="button"
                        onClick={() => apply.mutate(c.id)}
                        className="text-xs font-semibold text-nipost-green hover:underline"
                        data-testid="tier-apply-btn"
                      >
                        Apply
                      </button>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}
    </div>
  );
}
